import React, { useEffect, useState, useCallback } from 'react';
import { api, fmtMoney, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { FileText, Download, Printer, ChevronRight, BarChart2, TrendingUp, Mail, Users, Activity, GitBranch } from 'lucide-react';

const REPORT_TYPES = [
  { id: 'pipeline',   label: 'Pipeline Summary',   desc: 'Stage values, deals, weighted revenue',     Icon: BarChart2 },
  { id: 'forecast',   label: 'Revenue Forecast',   desc: 'Probability-weighted projections',          Icon: TrendingUp },
  { id: 'email',      label: 'Email Performance',  desc: 'Open rates, clicks, conversions',           Icon: Mail },
  { id: 'team',       label: 'Team Performance',   desc: 'Rep metrics, win rates, revenue',           Icon: Users },
  { id: 'contact',    label: 'Contact Activity',   desc: 'Stage distribution and engagement',         Icon: Activity },
  { id: 'conversion', label: 'Stage Conversion',   desc: 'Funnel analysis, drop-off rates',           Icon: GitBranch },
];

const PIE_COLORS_LIGHT = ['#ff9200', '#3368b8', '#64cb13', '#8762ae', '#f87171', '#ffa030'];
const PIE_COLORS_DARK  = ['#ff9200', '#70a0f0', '#4ade80', '#b48ae0', '#f87171', '#ffa030'];

function csvDownload(rows, cols, filename) {
  const header = cols.join(',');
  const body = rows.map((r) => cols.map((c) => `"${r[c] ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

const Reports = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [reportType, setReportType] = useState('pipeline');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [generated, setGenerated] = useState(null);

  const generate = useCallback(async () => {
    if (!lob) return;
    setLoading(true); setErr(''); setReport(null);
    try {
      const { data } = await api.get(`/reports/${reportType}?lob_id=${lob.id}`);
      setReport(data);
      setGenerated(new Date().toLocaleString());
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  }, [lob?.id, reportType]);

  const exportCsv = () => {
    if (!report) return;
    if (reportType === 'pipeline') csvDownload(report.by_stage, ['key', 'label', 'count', 'value', 'weighted'], 'pipeline.csv');
    else if (reportType === 'team') csvDownload(report.rows, ['owner', 'total', 'won', 'open', 'win_rate', 'revenue', 'pipeline'], 'team.csv');
    else if (reportType === 'email') csvDownload(report.campaigns, ['name', 'channel', 'status', 'sent', 'open_rate', 'click_rate', 'conv_rate'], 'email.csv');
    else if (reportType === 'forecast') csvDownload(report.deals, ['name', 'amount', 'stage_label', 'prob', 'weighted'], 'forecast.csv');
    else if (reportType === 'contact') csvDownload(report.contacts, ['first', 'last', 'email', 'phone', 'company', 'stage', 'amount'], 'contacts.csv');
    else if (reportType === 'conversion') csvDownload(report.funnel, ['stage', 'key', 'count', 'prob'], 'conversion.csv');
  };

  if (!lob) return null;

  return (
    <>
      <div className="page-toolbar" data-testid="reports-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Reports</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={generate} data-testid="generate-report-btn">
          {loading ? 'Generating…' : 'Generate report'}
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="report-error">{err}</div>}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div style={{ width: 240, flexShrink: 0, borderRight: '0.5px solid var(--stroke-lighter)', background: 'var(--bg-white)', overflow: 'auto' }}>
          <div style={{ padding: '10px 12px 6px' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-faded)', letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 8 }}>Report Type</div>
            {REPORT_TYPES.map((rt) => {
              const active = reportType === rt.id;
              return (
                <div key={rt.id}
                     onClick={() => { setReportType(rt.id); setReport(null); }}
                     data-testid={`report-type-${rt.id}`}
                     style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 2,
                              background: active ? aBg : 'transparent',
                              border: `0.5px solid ${active ? lob.accent + '40' : 'transparent'}` }}>
                  <rt.Icon size={13} style={{ color: active ? lob.accent : 'var(--text-soft)', marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: active ? aTc : 'var(--text-900)' }}>{rt.label}</div>
                    <div style={{ fontSize: 9, color: active ? aTc : 'var(--text-soft)', marginTop: 1 }}>{rt.desc}</div>
                  </div>
                  {active && <ChevronRight size={10} style={{ color: lob.accent, marginLeft: 'auto', marginTop: 3 }} />}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg-weak)', padding: '14px 16px' }}>
          {loading && <div className="empty-state">Loading…</div>}
          {!loading && !report && (
            <div className="empty-state" data-testid="report-empty">
              <BarChart2 size={36} style={{ opacity: 0.25 }} />
              <div className="ttl">Select a report and click Generate</div>
              <div className="sub">{REPORT_TYPES.find((r) => r.id === reportType)?.desc}</div>
              <button className="btn-primary" style={{ marginTop: 14 }} onClick={generate} data-testid="empty-generate-btn">
                Generate {REPORT_TYPES.find((r) => r.id === reportType)?.label}
              </button>
            </div>
          )}
          {report && (
            <div data-testid="report-output">
              <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{REPORT_TYPES.find((r) => r.id === reportType)?.label}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-faded)' }}>{lob.label} · Generated {generated}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="dp-btn" onClick={exportCsv} data-testid="export-csv-btn"><Download size={11} style={{ marginRight: 4 }} />CSV</button>
                  <button className="dp-btn" onClick={() => window.print()} data-testid="print-report-btn"><Printer size={11} style={{ marginRight: 4 }} />Print</button>
                </div>
              </div>
              {reportType === 'pipeline' && <PipelineReport report={report} accent={lob.accent} darkMode={darkMode} />}
              {reportType === 'forecast' && <ForecastReport report={report} accent={lob.accent} darkMode={darkMode} />}
              {reportType === 'email' && <EmailReport report={report} accent={lob.accent} darkMode={darkMode} />}
              {reportType === 'team' && <TeamReport report={report} accent={lob.accent} darkMode={darkMode} />}
              {reportType === 'contact' && <ContactReport report={report} accent={lob.accent} darkMode={darkMode} />}
              {reportType === 'conversion' && <ConversionReport report={report} accent={lob.accent} darkMode={darkMode} />}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const KpiGrid = ({ items }) => (
  <div className="kpi-grid" style={{ marginBottom: 10 }}>
    {items.map((k, i) => (
      <div className="kpi-card" key={i} data-testid={`report-kpi-${i}`}>
        <div className="kpi-label">{k.l}</div>
        <div className="kpi-value" style={{ fontSize: 18 }}>{k.v}</div>
      </div>
    ))}
  </div>
);

const chartTextColor = (dark) => dark ? '#5a7888' : '#777e83';
const chartGridColor = (dark) => dark ? '#1e3040' : '#e2e7ea';

const PipelineReport = ({ report, accent, darkMode }) => {
  const k = report.kpis;
  const chartData = report.by_stage.map((s) => ({ name: s.label, value: Math.round(s.value / 1000), weighted: Math.round(s.weighted / 1000) }));
  const pie = report.type_breakdown.map((t) => ({ name: t.name, value: Math.round(t.value / 1000) }));
  const colors = darkMode ? PIE_COLORS_DARK : PIE_COLORS_LIGHT;
  return (
    <>
      <KpiGrid items={[
        { l: 'Total Pipeline', v: fmtMoney(k.total_pipeline) },
        { l: 'Active Deals', v: k.active_deals },
        { l: 'Weighted', v: fmtMoney(k.weighted_pipeline) },
        { l: 'Avg Deal Size', v: fmtMoney(k.avg_deal_size) },
      ]} />
      <div className="card" style={{ marginBottom: 10, height: 240 }}>
        <div className="card-hd">Pipeline value by stage ($K)</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData}>
            <CartesianGrid stroke={chartGridColor(darkMode)} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
            <YAxis tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="value" name="Total" fill={accent} />
            <Bar dataKey="weighted" name="Weighted" fill={accent + '88'} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {pie.length > 0 && (
        <div className="card" style={{ marginBottom: 10, height: 240 }}>
          <div className="card-hd">Deal type breakdown ($K)</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pie} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {pie.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
              </Pie><Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      {report.deals?.length > 0 && (
        <div className="card">
          <div className="card-hd">Top deals</div>
          <table className="tbl">
            <thead><tr><th>Name</th><th>Type</th><th>Stage</th><th>Amount</th></tr></thead>
            <tbody>{report.deals.slice(0, 12).map((d) => (
              <tr key={d.id}><td>{d.name}</td><td>{d.type || '—'}</td><td>{d.stage}</td><td>{fmtMoney(d.amount)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </>
  );
};

const ForecastReport = ({ report, accent, darkMode }) => (
  <>
    <KpiGrid items={[
      { l: 'Weighted Pipeline', v: fmtMoney(report.kpis.weighted_pipeline) },
      { l: 'Expected this month', v: fmtMoney(report.kpis.expected_this_month) },
      { l: 'Coverage (×$1M)', v: report.kpis.pipeline_coverage },
      { l: 'At Risk', v: report.kpis.at_risk },
    ]} />
    <div className="card" style={{ height: 240, marginBottom: 10 }}>
      <div className="card-hd">Pipeline vs Weighted ($K)</div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={report.weighted_per_stage.map((s) => ({ stage: s.stage, total: Math.round(s.total / 1000), weighted: Math.round(s.weighted / 1000) }))}>
          <CartesianGrid stroke={chartGridColor(darkMode)} strokeDasharray="3 3" />
          <XAxis dataKey="stage" tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
          <YAxis tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
          <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
          <Bar dataKey="total" name="Total" fill={accent + '88'} />
          <Bar dataKey="weighted" name="Weighted" fill={accent} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </>
);

const EmailReport = ({ report, accent, darkMode }) => (
  <>
    <KpiGrid items={[
      { l: 'Total Sent', v: report.kpis.total_sent.toLocaleString() },
      { l: 'Avg Open', v: report.kpis.avg_open_rate + '%' },
      { l: 'Avg Click', v: report.kpis.avg_click_rate + '%' },
      { l: 'Conversions', v: report.kpis.total_conversions },
    ]} />
    {report.campaigns.length > 0 && (
      <div className="card" style={{ height: 240, marginBottom: 10 }}>
        <div className="card-hd">Open / Click / Conv (%)</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={report.campaigns.map((c) => ({ name: c.name.slice(0, 14), open: c.open_rate, click: c.click_rate, conv: c.conv_rate }))}>
            <CartesianGrid stroke={chartGridColor(darkMode)} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fill: chartTextColor(darkMode), fontSize: 9 }} /><YAxis tick={{ fill: chartTextColor(darkMode), fontSize: 9 }} />
            <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="open" name="Open %" fill={accent} />
            <Bar dataKey="click" name="Click %" fill="#3368b8" />
            <Bar dataKey="conv" name="Conv %" fill="#64cb13" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </>
);

const TeamReport = ({ report, accent, darkMode }) => (
  <>
    <KpiGrid items={[
      { l: 'Team Deals', v: report.kpis.total_deals },
      { l: 'Won', v: report.kpis.won },
      { l: 'Win Rate', v: report.kpis.win_rate + '%' },
      { l: 'Avg / Rep', v: report.kpis.avg_per_rep },
    ]} />
    <div className="card">
      <div className="card-hd">Per-rep stats</div>
      <table className="tbl">
        <thead><tr><th>Rep</th><th>Total</th><th>Won</th><th>Open</th><th>Win %</th><th>Revenue</th><th>Pipeline</th></tr></thead>
        <tbody>{report.rows.map((r) => (
          <tr key={r.owner_id}><td>{r.owner}</td><td>{r.total}</td><td>{r.won}</td><td>{r.open}</td><td>{r.win_rate}%</td><td>{fmtMoney(r.revenue)}</td><td>{fmtMoney(r.pipeline)}</td></tr>
        ))}</tbody>
      </table>
    </div>
  </>
);

const ContactReport = ({ report, accent, darkMode }) => (
  <>
    <KpiGrid items={[
      { l: 'Total Contacts', v: report.kpis.total_contacts },
      { l: 'With Phone', v: report.kpis.with_phone },
      { l: 'With Email', v: report.kpis.with_email },
      { l: 'With Company', v: report.kpis.with_company },
    ]} />
    {report.by_stage.length > 0 && (
      <div className="card" style={{ height: 240, marginBottom: 10 }}>
        <div className="card-hd">Contacts by stage</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={report.by_stage}>
            <CartesianGrid stroke={chartGridColor(darkMode)} strokeDasharray="3 3" />
            <XAxis dataKey="stage" tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
            <YAxis tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
            <Tooltip /><Bar dataKey="count" fill={accent} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </>
);

const ConversionReport = ({ report, accent, darkMode }) => (
  <>
    <KpiGrid items={[
      { l: 'Total Deals', v: report.total_deals },
      { l: 'Stages', v: report.funnel.length },
    ]} />
    <div className="card" style={{ height: 240 }}>
      <div className="card-hd">Funnel — count per stage</div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={report.funnel}>
          <CartesianGrid stroke={chartGridColor(darkMode)} strokeDasharray="3 3" />
          <XAxis dataKey="stage" tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
          <YAxis tick={{ fill: chartTextColor(darkMode), fontSize: 10 }} />
          <Tooltip /><Bar dataKey="count" fill={accent} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </>
);

export default Reports;
